import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { PlanwertDetail } from '../../domain/model/planwert-detail.model';
import { environment } from '../../../environments/environment';
import { Pageable } from '../../common/paging/pageable';
import { PlanwertFilter } from './planwert-filter';
import { Page } from '../../domain/utils/page';
import { PlanwertListEntry } from '../../domain/model/planwert-list-entry.model';
import { HttpParamHelperService } from '../utils/http-param-helper.service';
import { PlanwertSnapshot } from 'src/app/domain/model/planwert-snapshot.model';

@Injectable({
    providedIn: 'root',
})
export class PlanwertService {

    public refreshRequested$ = new Subject<void>();

    private readonly SERVICE_URL = `${environment.API_URL}/planwert`;

    private http = inject(HttpClient);

    public requestRefresh(): void {
        this.refreshRequested$.next();
    }

    public listPlanwerte(pageable?: Pageable, filter?: PlanwertFilter): Observable<Page<PlanwertListEntry>> {
        const params = this.toHttpParams(pageable, filter);
        return this.http.get<Page<PlanwertListEntry>>(this.SERVICE_URL, { params });
    }

    public getPlanwerte(dokumentenId: string, stadtwerkeId?: string): Observable<PlanwertDetail> {
        const params = this.toHttpParams(undefined, { stadtwerkeId });
        return this.http.get<PlanwertDetail>(`${this.SERVICE_URL}/${dokumentenId}`, { params });
    }

    public getSnapshot(filter?: PlanwertFilter): Observable<PlanwertSnapshot> {
        let params = this.toHttpParams(undefined, filter);
        return this.http.get<PlanwertSnapshot>(`${this.SERVICE_URL}/snapshot`, { params });
    }

    public createOrUpdatePlanwert(stadtwerkeId: string, dokumentenId: string, planwert: PlanwertDetail): Observable<PlanwertDetail> {
        const params = this.toHttpParams(undefined, { stadtwerkeId });
        return this.http.put<PlanwertDetail>(`${this.SERVICE_URL}/${dokumentenId}`, planwert, { params });
    }

    public deletePlanwerte(stadtwerkeId: string, dokumentenId: string): Observable<void> {
        const params = this.toHttpParams(undefined, { stadtwerkeId });
        return this.http.delete<void>(`${this.SERVICE_URL}/${dokumentenId}`, { params });
    }

    public listDokumentenIds(filter?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.SERVICE_URL}/dokumentenId`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId, filter }),
        });
    }

    public listTechnischeResourceId(filter?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.SERVICE_URL}/technischeResourceId`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId, filter }),
        });
    }

    public listBezVnbIntern(filter?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.SERVICE_URL}/bezVnbIntern`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId, filter }),
        });
    }

    public listNetzverknuepfungspunkte(filter?: string, stadtwerkeId?: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.SERVICE_URL}/netzverknuepfungspunkt`, {
            params: HttpParamHelperService.createHttpParams({ stadtwerkeId, filter }),
        });
    }

    private toHttpParams(pageable?: Pageable, filter?: PlanwertFilter): HttpParams {
        let params = new HttpParams({
            fromObject: pageable || {} as any,
        });

        if (filter) {
            if (filter.netzbetreiberName) {
                params = params.set('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.stadtwerkeId) {
                params = params.set('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.erzeugungsanlageDokumentenId) {
                params = params.set('erzeugungsanlageDokumentenId', filter.erzeugungsanlageDokumentenId);
            }
            if (filter.erzeugungsanlageTechnischeResourceId) {
                params = params.set('erzeugungsanlageTechnischeResourceId', filter.erzeugungsanlageTechnischeResourceId);
            }
            if (filter.erzeugungsanlageBezVnbIntern) {
                params = params.set('erzeugungsanlageBezVnbIntern', filter.erzeugungsanlageBezVnbIntern);
            }
            if (filter.netzzuordnungNetzverknuepfungspunkte) {
                params = params.set('netzzuordnungNetzverknuepfungspunkte', filter.netzzuordnungNetzverknuepfungspunkte);
            }
            if (filter.dataTransferDate && filter.dataTransferDate.start) {
                params = params.set('dataTransferDateStart', filter.dataTransferDate.start.toISOString(true));
            }
            if (filter.dataTransferDate && filter.dataTransferDate.end) {
                params = params.set('dataTransferDateEnd', filter.dataTransferDate.end.toISOString(true));
            }
            if (filter.type) {
                params = params.set('type', filter.type.toString());
            }
            if (filter.status) {
                params = params.set('status', filter.status.toString());
            }
        }

        return params;
    }
}
